import React, { useState, useContext, useEffect, useRef } from "react";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import axiosInstance from "../axiosConfig";
import { AuthContext } from "../AuthContext";

const payeeFields = { BANK_ACCOUNT_NO: "Bank Account No", IFSC_CODE: "IFSC Code", Gender: "Gender", Department: "Department" };

export default function PaidToForm() {
  const { user } = useContext(AuthContext);
  const fileRef = useRef();

  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const toggleSidebar = () => setIsSidebarVisible(!isSidebarVisible);

  const [workerList, setWorkerList] = useState([]);
  const [ledgerList, setLedgerList] = useState([]);
  const [budgetList, setBudgetList] = useState([]);
  const [loading, setLoading] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");

  const [form, setForm] = useState({
    WORKER_CODE: "",
    WORKER_NAME: "",
    LOGIC_ACCOUNT_CODE: "",
    LOGIC_ACCOUNT_NAME: "",
    BANK_ACCOUNT_NO: "",
    IFSC_CODE: "",
    Gender: "",
    Department: "",
    ledger_name: "",
    ledger_code: "",
    date: new Date().toISOString().split('T')[0],
    amount: "",
    amount_in_words: "",
    budget_head: "",
    employee_email: user?.email || "",
    employee_name: user?.name || "",
    details: ""
  });

  const [errors, setErrors] = useState({});
  const [file, setFile] = useState(null);

  useEffect(() => {
    loadBudget();
    if (user) {
        setForm(prev => ({
            ...prev,
            employee_email: user.email || "",
            employee_name: user.name || user.username || user.first_name || ""
        }));
    }
  }, [user]);

  const numberToWords = (num) => {
    if (!num) return "";
    const a = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
    const b = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
    const inWords = (n) => {
      if (n < 20) return a[n];
      if (n < 100) return b[Math.floor(n / 10)] + " " + a[n % 10];
      if (n < 1000) return a[Math.floor(n / 100)] + " Hundred " + inWords(n % 100);
      if (n < 100000) return inWords(Math.floor(n / 1000)) + " Thousand " + inWords(n % 1000);
      if (n < 10000000) return inWords(Math.floor(n / 100000)) + " Lakh " + inWords(n % 100000);
      return inWords(Math.floor(n / 10000000)) + " Crore " + inWords(n % 10000000);
    };
    return inWords(parseInt(num)) + " Only";
  };

  /* ================= FETCH ================= */
  const searchWorker = async (text) => {
    setSearchTerm(text);
    setForm(current => ({ ...current, WORKER_CODE: "", WORKER_NAME: "", LOGIC_ACCOUNT_CODE: "", LOGIC_ACCOUNT_NAME: "", BANK_ACCOUNT_NO: "", IFSC_CODE: "", Gender: "", Department: "" }));
    if (!text) return setWorkerList([]);
    try {
      const res = await axiosInstance.get('/api/paid-to/payee-search', { params: { q: text } });
      setWorkerList(res.data);
    } catch (err) {
      console.error("Worker search error:", err);
    }
  };

  const searchLedger = async (text) => {
    if (!text) return setLedgerList([]);
    try {
      const res = await axiosInstance.get('/api/ledger', { params: { search: text } });
      setLedgerList(res.data);
    } catch (err) {
      console.error("Ledger search error:", err);
    }
  };

  const loadBudget = async () => {
    try {
      const res = await axiosInstance.get("/api/budget-head");
      setBudgetList(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  /* ================= VALIDATION ================= */
  const missingPayeeFields = Object.keys(payeeFields).filter(
    key => key !== "Gender" && form.WORKER_CODE && !String(form[key] ?? "").trim()
  );
  const incompletePayee = missingPayeeFields.length > 0;
  const validateForm = () => {
    let newErrors = {};

    if (!form.WORKER_CODE) newErrors.WORKER_CODE = "Worker selection is required";
    if (!form.ledger_name) newErrors.ledger_name = "Ledger is required";
    if (!form.ledger_code) newErrors.ledger_code = "Ledger code required";
    if (!form.date) newErrors.date = "Date required";
    if (!form.amount || form.amount <= 0) newErrors.amount = "Valid amount required";
    if (!form.amount_in_words) newErrors.amount_in_words = "Amount in Words required";
    if (!form.budget_head) newErrors.budget_head = "Budget required";
    if (!form.employee_email) newErrors.employee_email = "Email required";
    if (!form.employee_name) newErrors.employee_name = "Name required";
    if (!form.details) newErrors.details = "Details required";
    
    // File upload validation per policy - optional or conditional. 
    // PDF says conditional, but let's assume it's like expense form if they want bill upload.
    // Assuming file is optional in this form unless specified. If required, uncomment below:
    // if (!file) newErrors.file = "Bill upload required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /* ================= SUBMIT ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm() || incompletePayee) return;

    try {
      setLoading(true);

      const data = new FormData();
      Object.keys(form).forEach(key => data.append(key, form[key]));
      if (file) data.append("file", file);

      const response = await axiosInstance.post("/api/paid-to/submit", data);
      
      alert(response.data.message || "Saved successfully");

      // RESET
      setForm({
        WORKER_CODE: "",
        WORKER_NAME: "",
        LOGIC_ACCOUNT_CODE: "",
        LOGIC_ACCOUNT_NAME: "",
        BANK_ACCOUNT_NO: "",
        IFSC_CODE: "",
        Gender: "",
        Department: "",
        ledger_name: "",
        ledger_code: "",
        date: new Date().toISOString().split('T')[0],
        amount: "",
        amount_in_words: "",
        budget_head: "",
        employee_email: user?.email || "",
        employee_name: user?.name || user?.username || user?.first_name || "",
        details: ""
      });

      setSearchTerm("");
      setErrors({});
      setFile(null);
      setWorkerList([]);
      setLedgerList([]);

      if (fileRef.current) fileRef.current.value = "";

    } catch (err) {
      console.error(err);
      alert("Error submitting request: " + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="d-flex dashboard">
      {/* SIDEBAR */}
      <div className={isSidebarVisible ? "sidebar-container" : "sidebar-hidden"}>
        <Sidebar />
      </div>

      <div className="flex-grow-1">
        {/* HEADER */}
        <Header toggleSidebar={toggleSidebar} isSidebarVisible={isSidebarVisible} />

        <main className="main-container p-4">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
            <h3 className="mb-0">PAID TO</h3>
            <a className="btn btn-outline-primary" href="/ai-form-assistant?form=paid-to">AI se form bharein · Chat / Voice</a>
          </div>

          <div className="card shadow border-0">
            <div className="card-header bg-dark text-white">
              Combined Worker and Expense Entry Form
            </div>

            <div className="card-body">
              <form onSubmit={handleSubmit}>
                <div className="row">
                  
                  {/* Worker Search */}
                  <div className="col-md-12 mb-3 position-relative">
                    <label>Search Logic Code / Tally Name</label>
                    <input
                      className={`form-control ${errors.WORKER_CODE && "is-invalid"}`}
                      value={searchTerm}
                      onChange={(e) => searchWorker(e.target.value)}
                      placeholder="Type at least 2 characters..."
                    />
                    {errors.WORKER_CODE && <div className="text-danger small">{errors.WORKER_CODE}</div>}

                    {workerList.length > 0 && (
                      <div className="list-group position-absolute w-100 shadow" style={{ zIndex: 1000, maxHeight: '200px', overflowY: 'auto' }}>
                        {workerList.map((w, i) => (
                          <button
                            key={i}
                            type="button"
                            className="list-group-item list-group-item-action"
                            onClick={() => {
                              setForm({
                                ...form,
                                WORKER_CODE: w.WORKER_CODE || "",
                                WORKER_NAME: w.WORKER_NAME || "",
                                LOGIC_ACCOUNT_CODE: w.LOGIC_ACCOUNT_CODE || "",
                                LOGIC_ACCOUNT_NAME: w.LOGIC_ACCOUNT_NAME || "",
                                BANK_ACCOUNT_NO: w.BANK_ACCOUNT_NO || "",
                                IFSC_CODE: w.IFSC_CODE || "",
                                Gender: w.Gender || "",
                                Department: w.Department || ""
                              });
                              setSearchTerm(`${w.LOGIC_ACCOUNT_CODE} - ${w.LOGIC_ACCOUNT_NAME}`);
                              setWorkerList([]);
                            }}
                          >
                            {w.LOGIC_ACCOUNT_CODE} - {w.LOGIC_ACCOUNT_NAME}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Worker Details (Read-only) */}
                  <div className="col-md-3 mb-3">
                    <label>Worker Code</label>
                    <input className="form-control bg-light" value={form.WORKER_CODE} readOnly />
                  </div>
                  <div className="col-md-3 mb-3">
                    <label>Worker Name</label>
                    <input className="form-control bg-light" value={form.WORKER_NAME} readOnly />
                  </div>
                  <div className="col-md-3 mb-3">
                    <label>Logic Account Code</label>
                    <input className="form-control bg-light" value={form.LOGIC_ACCOUNT_CODE} readOnly />
                  </div>
                  <div className="col-md-3 mb-3">
                    <label>Logic Account Name</label>
                    <input className="form-control bg-light" value={form.LOGIC_ACCOUNT_NAME} readOnly />
                  </div>

                  {Object.entries(payeeFields).map(([key, label]) => (
                    <div className="col-md-3 mb-3" key={key}>
                      <label htmlFor={key}>{label}{key !== "Gender" && " *"}</label>
                      <input id={key} className="form-control bg-light" value={form[key]} readOnly />
                      {missingPayeeFields.includes(key) && <small className="text-danger">Missing in payee master</small>}
                    </div>
                  ))}
                  {incompletePayee && (
                    <div className="col-12">
                      <div className="alert alert-warning" role="alert">
                        <strong>Employee details required</strong>
                        <p className="mb-0">
                          Open <a href="/users" target="_blank" rel="noopener noreferrer" className="alert-link">Manage Users (opens in a new tab)</a>,
                          select the employee, and choose <strong>Update User</strong>. Please complete the following
                          {missingPayeeFields.length === 1 ? " field" : " fields"}: <strong>{missingPayeeFields.map(key => payeeFields[key]).join(", ")}</strong>.
                          Save your changes, then return here and select the payee again to continue.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Expense Fields */}
                  <div className="col-md-6 mb-3 position-relative">
                    <label>Ledger Name</label>
                    <input
                      className={`form-control ${errors.ledger_name && "is-invalid"}`}
                      value={form.ledger_name}
                      onChange={(e) => {
                        setForm({ ...form, ledger_name: e.target.value });
                        searchLedger(e.target.value);
                      }}
                    />
                    {errors.ledger_name && <div className="text-danger small">{errors.ledger_name}</div>}

                    {ledgerList.length > 0 && (
                      <div className="list-group position-absolute w-100 shadow" style={{ zIndex: 1000, maxHeight: '200px', overflowY: 'auto' }}>
                        {ledgerList.map((l, i) => (
                          <button
                            key={i}
                            type="button"
                            className="list-group-item list-group-item-action"
                            onClick={() => {
                              setForm({
                                ...form,
                                ledger_name: l.CleanLogicName,
                                ledger_code: l.LogicCode
                              });
                              setLedgerList([]);
                            }}
                          >
                            {l.CleanLogicName}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="col-md-6 mb-3">
                    <label>Ledger Code</label>
                    <input className={`form-control bg-light ${errors.ledger_code && "is-invalid"}`} value={form.ledger_code} readOnly />
                    {errors.ledger_code && <div className="text-danger small">{errors.ledger_code}</div>}
                  </div>

                  <div className="col-md-4 mb-3">
                    <label>Date</label>
                    <input
                      type="date"
                      className={`form-control ${errors.date && "is-invalid"}`}
                      value={form.date}
                      onChange={(e) => setForm({ ...form, date: e.target.value })}
                    />
                    {errors.date && <div className="text-danger small">{errors.date}</div>}
                  </div>

                  <div className="col-md-4 mb-3">
                    <label>Amount</label>
                    <input
                      type="number"
                      step="0.01"
                      className={`form-control ${errors.amount && "is-invalid"}`}
                      value={form.amount}
                      onChange={(e) => {
                        const value = e.target.value;
                        setForm({ ...form, amount: value, amount_in_words: numberToWords(value) });
                      }}
                    />
                    {errors.amount && <div className="text-danger small">{errors.amount}</div>}
                  </div>

                  <div className="col-md-4 mb-3">
                    <label>Amount In Words</label>
                    <input
                      type="text"
                      className={`form-control bg-light ${errors.amount_in_words && "is-invalid"}`}
                      value={form.amount_in_words}
                      readOnly
                    />
                    {errors.amount_in_words && <div className="text-danger small">{errors.amount_in_words}</div>}
                  </div>

                  <div className="col-md-4 mb-3">
                    <label>Budget Head</label>
                    <select
                      className={`form-select ${errors.budget_head && "is-invalid"}`}
                      value={form.budget_head}
                      onChange={(e) => setForm({ ...form, budget_head: e.target.value })}
                    >
                      <option value="">Select Budget</option>
                      {budgetList.map((b, i) => (
                        <option key={i}>{b}</option>
                      ))}
                    </select>
                    {errors.budget_head && <div className="text-danger small">{errors.budget_head}</div>}
                  </div>

                  <div className="col-md-4 mb-3">
                    <label>Employee Email</label>
                    <input
                      type="email"
                      className={`form-control ${errors.employee_email && "is-invalid"}`}
                      value={form.employee_email}
                      onChange={(e) => setForm({ ...form, employee_email: e.target.value })}
                    />
                    {errors.employee_email && <div className="text-danger small">{errors.employee_email}</div>}
                  </div>

                  <div className="col-md-4 mb-3">
                    <label>Employee Name</label>
                    <input
                      className={`form-control ${errors.employee_name && "is-invalid"}`}
                      value={form.employee_name}
                      onChange={(e) => setForm({ ...form, employee_name: e.target.value })}
                    />
                    {errors.employee_name && <div className="text-danger small">{errors.employee_name}</div>}
                  </div>

                  <div className="col-md-12 mb-3">
                    <label>Details</label>
                    <textarea
                      className={`form-control ${errors.details && "is-invalid"}`}
                      value={form.details}
                      onChange={(e) => setForm({ ...form, details: e.target.value })}
                    />
                    {errors.details && <div className="text-danger small">{errors.details}</div>}
                  </div>

                  <div className="col-md-12 mb-3">
                    <label>Upload Bill</label>
                    <input
                      ref={fileRef}
                      type="file"
                      className={`form-control ${errors.file && "is-invalid"}`}
                      onChange={(e) => setFile(e.target.files[0])}
                    />
                    {errors.file && <div className="text-danger small">{errors.file}</div>}
                    {file && <div className="text-success small mt-1">{file.name}</div>}
                  </div>

                </div>

                <div className="text-end mt-3">
                  <button disabled={loading || Boolean(incompletePayee)} className="btn btn-primary">
                    {loading ? "Processing..." : "SAVE PAID TO ENTRY"}
                  </button>
                </div>

              </form>
            </div>
          </div>

        </main>
      </div>
    </div>
  );
}
